import { createSignal, onMount, For, Show } from 'solid-js';
import { useActiveBot } from '../../state/activeBot';
import { useBotFanOut } from '../../hooks/useBotFanOut';
import { BOT_ACTIONS } from '../../services/resourceActions';
import { useApi, isOk } from '../../api';
import { showConfirm } from '../../components/Dialog';
import { useToast } from '../../components/MessageBox';
import { useInventoryApi, type StorageInfo } from '../../services/inventoryApi';
import { Citybuild } from '../../models/Citybuild';
import Text from '../../components/Text';
import LocationPicker from '../../components/LocationPicker';
import { useTranslation } from '../../hooks/useTranslation';
import BotScoped from '../../components/BotScoped';
import ItemStackView from '../../components/ItemStackView';
import { fromIdentifier } from '../../services/itemRefAdapters';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import type { CraftingResolverService } from '../../services/craftingResolver';
import StorageSelect from '../../components/StorageSelect';
import styles from './Demolish.module.css';
import { firstError } from '../../api/envelope';

const citybuildOptions = Object.keys(Citybuild).filter(k => isNaN(Number(k))) as (keyof typeof Citybuild)[];

const MODE_STORAGE = 0;
const MODE_ENDER_CHEST = 1;

// Mirrors Remotex.Core.Models.MobType: the 1.8 entity type id, which is also the spawn egg's damage.
// Mobs without a 1.8 spawn egg of their own name the item that stands in for it.
const MOB_TYPES: { id: number; key: string; icon?: [string, number] }[] = [
    { id: 52, key: 'MOB_SPIDER' },
    { id: 59, key: 'MOB_CAVE_SPIDER' },
    { id: 51, key: 'MOB_SKELETON' },
    { id: 251, key: 'MOB_WITHER_SKELETON', icon: ['minecraft:skull', 1] },
    { id: 54, key: 'MOB_ZOMBIE' },
    { id: 252, key: 'MOB_ZOMBIE_VILLAGER', icon: ['minecraft:spawn_egg', 54] },
    { id: 57, key: 'MOB_ZOMBIE_PIGMAN' },
    { id: 50, key: 'MOB_CREEPER' },
    { id: 58, key: 'MOB_ENDERMAN' },
    { id: 61, key: 'MOB_BLAZE' },
    { id: 66, key: 'MOB_WITCH' },
    { id: 55, key: 'MOB_SLIME' },
    { id: 62, key: 'MOB_MAGMA_CUBE' },
    { id: 56, key: 'MOB_GHAST' },
    { id: 60, key: 'MOB_SILVERFISH' },
    { id: 67, key: 'MOB_ENDERMITE' },
    { id: 68, key: 'MOB_GUARDIAN' },
    { id: 253, key: 'MOB_ELDER_GUARDIAN', icon: ['minecraft:spawn_egg', 68] },
    { id: 65, key: 'MOB_BAT' },
    { id: 99, key: 'MOB_IRON_GOLEM' },
    { id: 97, key: 'MOB_SNOW_GOLEM', icon: ['minecraft:pumpkin', 0] },
    { id: 90, key: 'MOB_PIG' },
    { id: 92, key: 'MOB_COW' },
    { id: 96, key: 'MOB_MOOSHROOM' },
    { id: 91, key: 'MOB_SHEEP' },
    { id: 93, key: 'MOB_CHICKEN' },
    { id: 101, key: 'MOB_RABBIT' },
    { id: 94, key: 'MOB_SQUID' },
    { id: 95, key: 'MOB_WOLF' },
    { id: 98, key: 'MOB_OCELOT' },
    { id: 100, key: 'MOB_HORSE' },
    { id: 120, key: 'MOB_VILLAGER' },
];
const DEFAULT_MOB_TYPE = 52;
const SPAWN_EGG = 'minecraft:spawn_egg';

const mobIcon = (mobType: number, resolver: CraftingResolverService | null) => {
    const [identifier, meta] = MOB_TYPES.find(mob => mob.id === mobType)?.icon ?? [SPAWN_EGG, mobType];
    return fromIdentifier(identifier, meta, resolver);
};

interface PokeballConfig {
    id: string;
    name: string;
    citybuild: number;
    citybuildName: string;
    location: string;
    mobType: number;
    captureThreshold: number;
    radius: number;
    mode: number;
    storageId: string;
    minFreeSlots: number;
    emptyBallsToCarry: number;
    isEnabled: boolean;
    capturedMobs: number;
    capturedMobsThisMonth: number;
    createdAt: string;
}

export default function PokeballPage() {
    return <BotScoped groupAware><PokeballInner /></BotScoped>;
}

function PokeballInner() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const resolver = useCraftingResolver();

    const [configs, setConfigs] = createSignal<PokeballConfig[]>([]);
    const [storages, setStorages] = createSignal<StorageInfo[]>([]);
    const [loading, setLoading] = createSignal(true);

    const [showForm, setShowForm] = createSignal(false);
    const [editId, setEditId] = createSignal<string | null>(null);
    const [formName, setFormName] = createSignal('');
    const [formCitybuild, setFormCitybuild] = createSignal(0);
    const [formLocation, setFormLocation] = createSignal('');
    const [formMobType, setFormMobType] = createSignal(DEFAULT_MOB_TYPE);
    const [formThreshold, setFormThreshold] = createSignal(1000);
    const [formRadius, setFormRadius] = createSignal(20);
    const [formMode, setFormMode] = createSignal(MODE_STORAGE);
    const [formStorageId, setFormStorageId] = createSignal('');
    const [formMinFreeSlots, setFormMinFreeSlots] = createSignal(4);
    const [formBallsToCarry, setFormBallsToCarry] = createSignal(16);
    const [formIsEnabled, setFormIsEnabled] = createSignal(true);
    const [saving, setSaving] = createSignal(false);

    const { botId: activeBotId, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const botId = () => activeBotId() ?? '';
    const inv = useInventoryApi();

    const formValid = () => formMode() === MODE_ENDER_CHEST || formStorageId() !== '';

    onMount(async () => {
        try {
            const [cfgRes, storageList] = await Promise.all([
                api.pokeball.getPokeballConfigs(),
                inv.fetchStorages(),
            ]);
            if (isOk(cfgRes.data)) setConfigs((cfgRes.data.value.configs ?? []) as PokeballConfig[]);
            else toast?.error(firstError(cfgRes.data) ?? t('POKEBALL_LOAD_FAILED'));
            setStorages(storageList);
        } catch (e: any) {
            toast?.error(e?.message ?? t('POKEBALL_LOAD_FAILED'));
        } finally { setLoading(false); }
    });

    async function loadConfigs() {
        const res = await api.pokeball.getPokeballConfigs();
        if (isOk(res.data)) setConfigs((res.data.value.configs ?? []) as PokeballConfig[]);
    }

    function openCreate() {
        setEditId(null);
        setFormName('');
        setFormCitybuild(0);
        setFormLocation('');
        setFormMobType(DEFAULT_MOB_TYPE);
        setFormThreshold(1000);
        setFormRadius(20);
        setFormMode(MODE_STORAGE);
        setFormStorageId(storages()[0]?.id ?? '');
        setFormMinFreeSlots(4);
        setFormBallsToCarry(16);
        setFormIsEnabled(true);
        setShowForm(true);
    }

    function openEdit(c: PokeballConfig) {
        setEditId(c.id);
        setFormName(c.name);
        setFormCitybuild(c.citybuild);
        setFormLocation(c.location);
        setFormMobType(c.mobType);
        setFormThreshold(c.captureThreshold);
        setFormRadius(c.radius);
        setFormMode(c.mode);
        setFormStorageId(c.mode === MODE_STORAGE ? c.storageId : (storages()[0]?.id ?? ''));
        setFormMinFreeSlots(c.minFreeSlots);
        setFormBallsToCarry(c.emptyBallsToCarry);
        setFormIsEnabled(c.isEnabled);
        setShowForm(true);
    }

    async function save(e: Event) {
        e.preventDefault();
        if (formMode() === MODE_STORAGE && !formStorageId()) { toast?.error(t('POKEBALL_SELECT_STORAGE')); return; }
        setSaving(true);
        const body = {
            name: formName().trim() || t('POKEBALL_DEFAULT_NAME'),
            citybuild: formCitybuild(),
            location: formLocation().trim(),
            mobType: formMobType(),
            captureThreshold: formThreshold(),
            radius: formRadius(),
            mode: formMode(),
            storageId: formMode() === MODE_STORAGE ? formStorageId() : '00000000-0000-0000-0000-000000000000',
            minFreeSlots: formMinFreeSlots(),
            emptyBallsToCarry: formBallsToCarry(),
            isEnabled: formIsEnabled(),
        };
        try {
            const id = editId();
            const res = id
                ? await api.pokeball.updatePokeballConfig(id, body)
                : await api.pokeball.createPokeballConfig(body);
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t('POKEBALL_SAVE_FAILED'));
                return;
            }
            setShowForm(false);
            toast?.info(id ? t('POKEBALL_UPDATED') : t('POKEBALL_CREATED'));
            await loadConfigs();
        } finally { setSaving(false); }
    }

    async function deleteConfig(id: string) {
        const confirmed = await showConfirm(t('POKEBALL_DELETE_CONFIRM'), true);
        if (!confirmed) return;
        const res = await api.pokeball.deletePokeballConfig(id);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) ?? t('POKEBALL_DELETE_FAILED'));
            return;
        }
        toast?.info(t('POKEBALL_DELETED'));
        await loadConfigs();
    }

    async function runConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.Control);
        if (targets.length === 0) { toast?.error(t('POKEBALL_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.pokeball.runPokeballConfig(id, { botId: targetBotId }),
            { success: t('POKEBALL_STARTED'), failure: t('POKEBALL_START_FAILED') });
    }

    async function stopConfig(id: string) {
        const targets = targetIds(BOT_ACTIONS.Control);
        if (targets.length === 0) { toast?.error(t('POKEBALL_PICK_BOT')); return; }
        await fanOut.run(targets, targetBotId => api.pokeball.stopPokeball(id, { botId: targetBotId }),
            { success: t('POKEBALL_STOPPED'), failure: t('POKEBALL_STOP_FAILED') });
    }

    function mobName(mobType: number) {
        return t(MOB_TYPES.find(mob => mob.id === mobType)?.key ?? 'POKEBALL_UNKNOWN_MOB');
    }

    function storageName(id: string) {
        return storages().find(s => s.id === id)?.name ?? t('POKEBALL_UNKNOWN_STORAGE');
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="POKEBALL_TITLE"/></h1>
                <button class="btn btn-primary" onClick={openCreate}>
                    <i class="fa-solid fa-plus" /> <Text val="POKEBALL_NEW_CONFIG"/>
                </button>
            </div>
            <p class={styles.subtitle}><Text val="POKEBALL_SUBTITLE"/></p>

            <Show when={loading()}>
                <div class={styles.empty}>
                    <i class="fa-solid fa-spinner fa-spin fa-2x" />
                </div>
            </Show>

            <Show when={!loading()}>
                <Show when={showForm()}>
                    <form class={`fcard ${styles.form}`} onSubmit={save}>
                        <h2 class={styles.formTitle}>
                            {editId() ? <Text val="POKEBALL_EDIT"/> : <Text val="POKEBALL_CREATE"/>}
                        </h2>

                        <div class={styles.formGrid}>
                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_NAME"/></span>
                                <input class={styles.input} value={formName()} onInput={e => setFormName(e.currentTarget.value)} placeholder={t('POKEBALL_NAME_PLACEHOLDER')} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_CITYBUILD"/></span>
                                <select class={styles.input} value={formCitybuild()} onChange={e => setFormCitybuild(parseInt(e.currentTarget.value))}>
                                    <For each={citybuildOptions}>{(cb) => <option value={Citybuild[cb]}>{cb}</option>}</For>
                                </select>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_LOCATION"/></span>
                                <LocationPicker value={formLocation()} onChange={setFormLocation} optional />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_MOB"/></span>
                                <div class="hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                    <ItemStackView stack={mobIcon(formMobType(), resolver())} size={24} bare />
                                    <select class={styles.input} style={{ flex: '1' }} value={formMobType()} onChange={e => setFormMobType(parseInt(e.currentTarget.value))}>
                                        <For each={MOB_TYPES}>{(mob) => <option value={mob.id}>{t(mob.key)}</option>}</For>
                                    </select>
                                </div>
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_THRESHOLD"/></span>
                                <input class={styles.input} type="number" min="1" max="1000" value={formThreshold()} onInput={e => setFormThreshold(parseInt(e.currentTarget.value) || 1000)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_RADIUS"/></span>
                                <input class={styles.input} type="number" min="1" max="64" value={formRadius()} onInput={e => setFormRadius(parseInt(e.currentTarget.value) || 20)} />
                            </label>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_MODE"/></span>
                                <select class={styles.input} value={formMode()} onChange={e => setFormMode(parseInt(e.currentTarget.value))}>
                                    <option value={MODE_STORAGE}>{t('POKEBALL_MODE_STORAGE')}</option>
                                    <option value={MODE_ENDER_CHEST}>{t('POKEBALL_MODE_ENDER_CHEST')}</option>
                                </select>
                            </label>

                            <Show when={formMode() === MODE_STORAGE}>
                                <label class={styles.field}>
                                    <span class={styles.fieldLabel}><Text val="POKEBALL_F_STORAGE"/></span>
                                    <StorageSelect
                                        storages={storages()}
                                        value={formStorageId()}
                                        onChange={setFormStorageId}
                                        placeholder={t('POKEBALL_SELECT_STORAGE_OPTION')}
                                    />
                                </label>

                                <label class={styles.field}>
                                    <span class={styles.fieldLabel}><Text val="POKEBALL_F_CARRY"/></span>
                                    <input class={styles.input} type="number" min="1" max="64" value={formBallsToCarry()} onInput={e => setFormBallsToCarry(parseInt(e.currentTarget.value) || 16)} />
                                </label>
                            </Show>

                            <label class={styles.field}>
                                <span class={styles.fieldLabel}><Text val="POKEBALL_F_MINFREE"/></span>
                                <input class={styles.input} type="number" min="0" max="27" value={formMinFreeSlots()} onInput={e => setFormMinFreeSlots(parseInt(e.currentTarget.value) || 0)} />
                            </label>
                        </div>
                        <span class={styles.fieldHint}>
                            {formMode() === MODE_STORAGE ? <Text val="POKEBALL_MODE_STORAGE_HINT"/> : <Text val="POKEBALL_MODE_ENDER_CHEST_HINT"/>}
                        </span>

                        <label class={styles.checkField}>
                            <input type="checkbox" checked={formIsEnabled()} onChange={e => setFormIsEnabled(e.currentTarget.checked)} />
                            <span><Text val="POKEBALL_F_ENABLED"/></span>
                        </label>

                        <div class={styles.formActions}>
                            <button type="button" class="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving()}>
                                <Text val="CANCEL"/>
                            </button>
                            <button type="submit" class="btn btn-primary" disabled={saving() || !formValid()}>
                                <Show when={saving()} fallback={<><i class="fa-solid fa-floppy-disk" /> <Text val="SAVE"/></>}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="SAVE"/>
                                </Show>
                            </button>
                        </div>
                    </form>
                </Show>

                <Show when={configs().length === 0 && !showForm()}>
                    <div class={styles.empty}>
                        <i class="fa-solid fa-circle-dot" />
                        <p><Text val="POKEBALL_EMPTY"/></p>
                    </div>
                </Show>

                <div class={styles.grid}>
                    <For each={configs()}>
                        {(c) => (
                            <div class={`fcard ${styles.card} ${!c.isEnabled ? styles.cardDisabled : ''}`}>
                                <div class={styles.cardHeader}>
                                    <div>
                                        <div class={styles.cardName}>{c.name}</div>
                                        <div class={styles.cardMeta}>
                                            {c.citybuildName}
                                            {c.location ? ` · ${c.location}` : ''}
                                        </div>
                                    </div>
                                    <span class={c.isEnabled ? styles.pillEnabled : styles.pillDisabled}>
                                        {c.isEnabled ? <Text val="POKEBALL_ENABLED"/> : <Text val="POKEBALL_DISABLED"/>}
                                    </span>
                                </div>

                                <div class={styles.cardBody}>
                                    <div class={styles.infoRow}>
                                        <ItemStackView stack={mobIcon(c.mobType, resolver())} size={16} bare />
                                        {t('POKEBALL_INFO_TARGET', mobName(c.mobType), String(c.captureThreshold))}
                                    </div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-circle-dot" /> {t('POKEBALL_INFO_RADIUS', String(c.radius))}</div>
                                    <div class={styles.infoRow}>
                                        <i class={c.mode === MODE_STORAGE ? 'fa-solid fa-warehouse' : 'fa-solid fa-box-archive'} />
                                        {c.mode === MODE_STORAGE ? storageName(c.storageId) : t('POKEBALL_MODE_ENDER_CHEST')}
                                    </div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-box-open" /> {t('POKEBALL_INFO_MINFREE', String(c.minFreeSlots))}</div>
                                    <div class={styles.infoRow}><i class="fa-solid fa-trophy" /> {t('POKEBALL_INFO_CAPTURED', c.capturedMobs.toLocaleString(), c.capturedMobsThisMonth.toLocaleString())}</div>
                                </div>

                                <div class={styles.cardActions}>
                                    <button class="btn btn-primary" onClick={() => runConfig(c.id)} disabled={!botId()} title={t('RUN')}>
                                        <i class="fa-solid fa-play" /> <Text val="POKEBALL_RUN"/>
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => stopConfig(c.id)} disabled={!botId()} title={t('STOP')}>
                                        <i class="fa-solid fa-stop" />
                                    </button>
                                    <button class="btn btn-secondary" onClick={() => openEdit(c)} title={t('EDIT')}>
                                        <i class="fa-solid fa-pen" />
                                    </button>
                                    <button class="btn btn-danger" onClick={() => deleteConfig(c.id)} title={t('DELETE')}>
                                        <i class="fa-solid fa-trash" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </For>
                </div>
            </Show>
        </div>
    );
}
